import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import VisitRequestModal from "@/components/VisitRequestModal";
import ReportModal from "@/components/ReportModal";
import RescheduleModal from "@/components/RescheduleModal";
import ParentCalendar from "@/components/ParentCalendar";
import { StatusBadge, TimelinessBadge, VisitTypeBadge, QualitySummary, RescheduleNote } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarPlus, MapPin, Clock, CheckCircle2, LogIn, LogOut, CalendarDays, TrendingUp, FileText, CalendarClock, List, CalendarRange, UserX } from "lucide-react";

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
        <Icon className={`h-4 w-4 ${tone}`} />
      </div>
      <p className="mt-2 font-display text-3xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

export default function ParentDashboard() {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [stats, setStats] = useState({});
  const [reports, setReports] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [visitView, setVisitView] = useState("list");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [v, s, r] = await Promise.all([api.get("/visits"), api.get("/stats"), api.get("/reports")]);
      setVisits(v.data);
      setStats(s.data);
      setReports(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const action = async (id, kind) => {
    setBusy(id + kind);
    try {
      if (kind === "checkin") await api.put(`/visits/${id}/checkin`);
      else await api.put(`/visits/${id}/checkout`, {});
      toast.success(kind === "checkin" ? "Checked in" : "Checked out — visit completed");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header subtitle="Parent Portal" />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div className="animate-fade-up">
            <h1 className="font-display text-3xl font-bold text-slate-900">Welcome, {user.name.split(" ")[0]}</h1>
            <p className="mt-1 text-slate-500">Schedule visits and track your time together.</p>
          </div>
          <Button data-testid="new-visit-btn" onClick={() => setModalOpen(true)} className="bg-[#1e3a8a] hover:bg-[#172554]">
            <CalendarPlus className="mr-2 h-4 w-4" /> Request Visit
          </Button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <Stat icon={CalendarDays} label="Total Visits" value={stats.total ?? 0} tone="text-[#1e3a8a]" />
          <Stat icon={CheckCircle2} label="Completed" value={stats.completed ?? 0} tone="text-emerald-600" />
          <Stat icon={TrendingUp} label="Approval Rate" value={`${stats.approval_rate ?? 0}%`} tone="text-[#0d9488]" />
          <Stat icon={Clock} label="On-Time" value={stats.on_time ?? 0} tone="text-orange-500" />
          <Stat icon={UserX} label="No-Shows" value={stats.no_show ?? 0} tone="text-red-500" />
        </div>

        {reports.length > 0 && (
          <>
            <h2 className="mt-10 flex items-center gap-2 font-display text-xl font-semibold text-slate-900">
              <FileText className="h-5 w-5 text-[#0d9488]" /> Your Quality Reports
            </h2>
            <p className="text-sm text-slate-500">Reports finalized by your case worker. Read-only — you can view and print.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="parent-reports-list">
              {reports.map((r) => (
                <button
                  key={r.id}
                  data-testid={`parent-report-${r.id}`}
                  onClick={() => { setActiveReport(r); setReportOpen(true); }}
                  className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#0d9488] hover:shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">Finalized</span>
                    <FileText className="h-4 w-4 text-slate-400" />
                  </div>
                  <p className="mt-3 font-display font-semibold text-slate-900">Quality of Visit Report</p>
                  <p className="text-xs text-slate-500">By {r.generated_by}</p>
                  <p className="mt-1 text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</p>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="mt-10 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold text-slate-900">Your Visits</h2>
          <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
            <button
              data-testid="view-list-btn"
              onClick={() => setVisitView("list")}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${visitView === "list" ? "bg-[#1e3a8a] text-white" : "text-slate-500 hover:text-slate-700"}`}
            >
              <List className="h-4 w-4" /> List
            </button>
            <button
              data-testid="view-calendar-btn"
              onClick={() => setVisitView("calendar")}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${visitView === "calendar" ? "bg-[#1e3a8a] text-white" : "text-slate-500 hover:text-slate-700"}`}
            >
              <CalendarRange className="h-4 w-4" /> Calendar
            </button>
          </div>
        </div>

        {visitView === "calendar" && <div className="mt-4"><ParentCalendar visits={visits} /></div>}

        {visitView === "list" && (
        <div className="mt-4 space-y-3" data-testid="parent-visits-list">
          {visits.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <CalendarDays className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 font-medium text-slate-600">No visits yet</p>
              <p className="text-sm text-slate-400">Request your first visit to get started.</p>
            </div>
          )}
          {visits.map((v) => (
            <div key={v.id} data-testid={`visit-card-${v.id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-lg font-semibold text-slate-900">Visit with {v.child_name}</h3>
                    <StatusBadge status={v.status} testid={`status-${v.id}`} />
                    <VisitTypeBadge type={v.visit_type} />
                    {v.timeliness === "no_show" && <TimelinessBadge value="no_show" />}
                    {v.completed && <span className="rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-700">Completed</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
                    <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />{v.visit_date}</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{v.start_time}–{v.end_time}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{v.location}</span>
                  </div>
                  {v.status === "not_approved" && v.review_note && (
                    <p className="mt-2 rounded-md bg-red-50 px-3 py-1.5 text-sm text-red-700">Reason: {v.review_note}</p>
                  )}
                  <RescheduleNote history={v.reschedule_history} />
                  {v.check_in_time && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                      Checked in <TimelinessBadge value={v.timeliness} />
                    </div>
                  )}
                  {v.assessment && <QualitySummary assessment={v.assessment} testid={`quality-${v.id}`} />}
                </div>
                <div className="flex gap-2">
                  {v.status === "approved" && !v.check_in_time && (
                    <Button size="sm" data-testid={`checkin-btn-${v.id}`} disabled={busy === v.id + "checkin"} onClick={() => action(v.id, "checkin")} className="bg-[#0d9488] hover:bg-[#115e59]">
                      <LogIn className="mr-1.5 h-4 w-4" /> Check In
                    </Button>
                  )}
                  {v.check_in_time && !v.completed && (
                    <Button size="sm" variant="outline" data-testid={`checkout-btn-${v.id}`} disabled={busy === v.id + "checkout"} onClick={() => action(v.id, "checkout")}>
                      <LogOut className="mr-1.5 h-4 w-4" /> Check Out
                    </Button>
                  )}
                  {!v.completed && !v.check_in_time && (
                    <Button size="sm" variant="outline" data-testid={`reschedule-btn-${v.id}`} onClick={() => { setRescheduleTarget(v); setRescheduleOpen(true); }}>
                      <CalendarClock className="mr-1.5 h-4 w-4" /> Reschedule
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </main>

      <VisitRequestModal open={modalOpen} onOpenChange={setModalOpen} defaultChild={user.child_name} onCreated={load} />
      <ReportModal report={activeReport} open={reportOpen} onOpenChange={setReportOpen} />
      <RescheduleModal open={rescheduleOpen} onOpenChange={setRescheduleOpen} visit={rescheduleTarget} onDone={load} />
    </div>
  );
}
